/**
 * A long entity form as tabbed sections, with a field search (ADR-35).
 *
 * Matt, 2026-08-22: "since the property data list has grown substantially, we
 * need to rewrite the form so that it has tabbed sections to keep it organized
 * and easier to work with editing/updating data on a single screen instead of
 * scrolling endlessly. Perhaps also include a quick search feature to allow the
 * user to quickly search for attributes they need to edit in the form."
 *
 * This supersedes ADR-34, which said the opposite. The objections there were
 * real and are not waved away — they are engineered against, because "the field
 * is on another tab" is only a problem if the app lets it stay hidden:
 *
 *   * A tab with a validation error carries a count, in the error colour. On
 *     submit the form JUMPS to the first tab with an error and focuses the
 *     field. So an error can never be sitting on a pane nobody is looking at.
 *   * A tab with unsaved changes carries a dot. So "did I change something over
 *     there?" is answerable without visiting.
 *   * The field search spans EVERY tab, not the visible one. Typing "alarm"
 *     finds the alarm fields wherever they live, and choosing one switches tab,
 *     scrolls to it, focuses it and flashes it. That is the feature that makes
 *     tabs cheaper than scrolling instead of merely different.
 *
 * Panels are kept MOUNTED and hidden with CSS, not unmounted. Unmounting would
 * throw away input state on every tab change and — worse — silently drop the
 * validation state this component depends on.
 */

import { clsx } from 'clsx';
import { Search, X } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type MutableRefObject,
} from 'react';

export interface FormTab {
  /** Stable key, used for the panel id and for error/dirty lookup. */
  readonly id: string;
  readonly label: string;
  /** Icon rendered before the label; hidden from assistive tech. */
  readonly icon?: ReactNode;
  readonly content: ReactNode;
  /**
   * The searchable field labels on this tab, in render order. Supplied rather
   * than scraped from the DOM so search works before a panel has ever been
   * shown, and so the label the user searches is the label they see.
   */
  readonly fields?: readonly string[];
  /** How many of this tab's fields currently fail validation. */
  readonly errorCount?: number;
  /** Whether anything on this tab differs from what was loaded. */
  readonly dirty?: boolean;
}

export interface TabbedFormHandle {
  /** Show a tab. Used by the parent to jump to the first failing tab. */
  readonly show: (tabId: string) => void;
}

/** A search hit: which tab, and the field label. */
interface FieldHit {
  readonly tabId: string;
  readonly tabLabel: string;
  readonly field: string;
}

/**
 * Find the input for a label and put the caret in it.
 *
 * By label text, because that is what the search matched and what the user
 * asked for; there is no field registry to look up and inventing one would mean
 * every form maintaining ids by hand. Scoped to the panel so an identical label
 * on another tab cannot win.
 */
function focusFieldByLabel(panel: HTMLElement | null, label: string): void {
  if (panel === null) return;
  const match = [...panel.querySelectorAll('label')].find(
    (node) => node.textContent.trim().replace(/\s*\*$/, '') === label,
  );
  const inputId = match?.getAttribute('for');
  const field =
    inputId !== null && inputId !== undefined
      ? panel.ownerDocument.getElementById(inputId)
      : (match?.querySelector('input, select, textarea') ?? null);
  if (field === null) return;
  field.scrollIntoView({ block: 'center', behavior: 'smooth' });
  (field as HTMLElement).focus({ preventScroll: true });
  // A brief ring: after a tab switch the caret alone is easy to lose, and this
  // is the moment the user most needs to see WHERE they landed.
  field.classList.add('ring-2', 'ring-brand-600', 'rounded-ctl');
  setTimeout(() => {
    field.classList.remove('ring-2', 'ring-brand-600', 'rounded-ctl');
  }, 1400);
}

export function TabbedForm({
  tabs,
  handleRef,
  toolbar,
  initialTabId,
}: {
  readonly tabs: readonly FormTab[];
  /** Set by the parent to drive the form from outside (jump to a tab). */
  readonly handleRef?: MutableRefObject<TabbedFormHandle | null> | undefined;
  /** Rendered at the right of the tab strip — a save button, a dirty count. */
  readonly toolbar?: ReactNode;
  /**
   * Which tab opens first. The INITIAL value only — after that the tab strip
   * owns it, so a parent reading a URL cannot fight the user's clicks.
   *
   * Added 2026-08-28 for the lease form (docs/17 3B.4), where "Revise abstract"
   * has to land on the abstract rather than on the first tab and leave the
   * reader to find it. An unknown id falls back to the first tab.
   */
  readonly initialTabId?: string | undefined;
}): ReactNode {
  const baseId = useId();
  const [activeId, setActiveId] = useState(
    (initialTabId !== undefined && tabs.some((tab) => tab.id === initialTabId)
      ? initialTabId
      : tabs[0]?.id) ?? '',
  );
  const [query, setQuery] = useState('');
  const [hitIndex, setHitIndex] = useState(0);
  const panelsRef = useRef<Record<string, HTMLDivElement | null>>({});
  const pendingFocus = useRef<FieldHit | null>(null);

  const show = useCallback((tabId: string) => {
    setActiveId(tabId);
  }, []);

  useEffect(() => {
    if (handleRef !== undefined) handleRef.current = { show };
  }, [handleRef, show]);

  const hits = useMemo<readonly FieldHit[]>(() => {
    const needle = query.trim().toLowerCase();
    if (needle === '') return [];
    return tabs.flatMap((tab) =>
      (tab.fields ?? [])
        .filter((field) => field.toLowerCase().includes(needle))
        .map((field) => ({ tabId: tab.id, tabLabel: tab.label, field })),
    );
  }, [query, tabs]);

  // Focus after the tab switch has rendered, not during it: the panel is
  // hidden until then and an element with no box cannot be scrolled to.
  useEffect(() => {
    const target = pendingFocus.current;
    if (target?.tabId !== activeId) return;
    pendingFocus.current = null;
    focusFieldByLabel(panelsRef.current[target.tabId] ?? null, target.field);
  }, [activeId]);

  const go = (hit: FieldHit): void => {
    pendingFocus.current = hit;
    if (hit.tabId === activeId) {
      focusFieldByLabel(panelsRef.current[hit.tabId] ?? null, hit.field);
      pendingFocus.current = null;
    } else {
      setActiveId(hit.tabId);
    }
    setQuery('');
    setHitIndex(0);
  };

  return (
    <div className="flex min-w-0 flex-col gap-3">
      {/* Search BESIDE the tab strip from 768 up, not above it: above, the
          results list dropped straight over the tabs — covering the navigation
          you are about to use. Beside, it falls over the panel instead. Below
          768 it takes its own row, because a strip that scrolls horizontally
          plus an input do not share a line. */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <div className="relative order-1 min-w-0 sm:order-2 sm:w-60 sm:shrink-0">
          <label className="sr-only" htmlFor={`${baseId}-find`}>
            Find a field
          </label>
          <Search
            size={16}
            aria-hidden
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink2"
          />
          <input
            id={`${baseId}-find`}
            value={query}
            placeholder="Find a field…"
            role="combobox"
            aria-expanded={hits.length > 0}
            aria-controls={`${baseId}-hits`}
            aria-autocomplete="list"
            autoComplete="off"
            onChange={(event) => {
              setQuery(event.target.value);
              setHitIndex(0);
            }}
            onKeyDown={(event) => {
              // Enter is handled FIRST, and unconditionally: with no hits the
              // early return below let it fall through and SUBMIT THE FORM,
              // which is a trap — someone looking for a field would save the
              // record instead. Caught by e2e.
              if (event.key === 'Enter') {
                event.preventDefault();
                const hit = hits[hitIndex];
                if (hit !== undefined) go(hit);
                return;
              }
              if (event.key === 'Escape') {
                setQuery('');
                return;
              }
              if (hits.length === 0) return;
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                setHitIndex((i) => (i + 1) % hits.length);
              } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                setHitIndex((i) => (i - 1 + hits.length) % hits.length);
              }
            }}
            className="keeper-ignore min-h-11 w-full rounded-ctl border border-control-line bg-card pl-9 pr-9 text-body text-ink placeholder:text-ink2"
          />
          {query === '' ? null : (
            <button
              type="button"
              aria-label="Clear field search"
              onClick={() => {
                setQuery('');
              }}
              className="absolute right-2 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-ctl text-ink2 hover:bg-surface-hover"
            >
              <X size={14} aria-hidden />
            </button>
          )}

          {hits.length > 0 ? (
            <ul
              id={`${baseId}-hits`}
              role="listbox"
              className="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-ctl border border-card-line bg-card py-1 shadow-lg"
            >
              {hits.map((hit, index) => (
                <li key={`${hit.tabId}:${hit.field}`} role="none">
                  <button
                    type="button"
                    role="option"
                    aria-selected={index === hitIndex}
                    onClick={() => {
                      go(hit);
                    }}
                    onMouseEnter={() => {
                      setHitIndex(index);
                    }}
                    className={clsx(
                      'flex min-h-10 w-full items-center justify-between gap-3 px-3 text-left text-body',
                      index === hitIndex ? 'bg-surface-hover text-ink' : 'text-ink',
                    )}
                  >
                    <span className="truncate">{hit.field}</span>
                    {/* Which tab it is on: the answer to "where is that field?"
                        is half the value of searching for it. */}
                    <span className="shrink-0 text-chip text-ink2">{hit.tabLabel}</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : query.trim() === '' ? null : (
            <p className="absolute z-20 mt-1 w-full rounded-ctl border border-card-line bg-card px-3 py-2 text-meta text-ink2">
              No field matches “{query.trim()}”
            </p>
          )}
        </div>
        {toolbar === undefined ? null : (
          <div className="order-3 shrink-0 self-center sm:self-end sm:pb-2">{toolbar}</div>
        )}

        {/* WRAPS from 768 up, scrolls below it. Seven tabs plus the search do
            not fit on one line inside a form's max width, and the scrolling
            version quietly pushed the last two tabs out of sight on a 1280px
            desktop — hiding navigation is worse than spending a second row on
            it. Below 768 the scroll wins instead: three wrapped rows would eat
            the screen a phone needs for the fields. */}
        <div
          role="tablist"
          aria-label="Form sections"
          className="order-2 -mx-1 flex min-w-0 grow gap-1 overflow-x-auto border-b border-hairline px-1 sm:order-1 sm:flex-wrap sm:overflow-x-visible"
        >
          {tabs.map((tab) => {
            const errors = tab.errorCount ?? 0;
            const active = tab.id === activeId;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                id={`${baseId}-tab-${tab.id}`}
                aria-selected={active}
                aria-controls={`${baseId}-panel-${tab.id}`}
                onClick={() => {
                  setActiveId(tab.id);
                }}
                className={clsx(
                  'flex min-h-11 shrink-0 items-center gap-1.5 border-b-2 px-3 text-body font-medium',
                  active
                    ? 'border-gold-deep text-ink'
                    : 'border-transparent text-ink2 hover:text-ink',
                )}
              >
                {tab.icon === undefined ? null : <span aria-hidden>{tab.icon}</span>}
                {tab.label}
                {errors > 0 ? (
                  <span
                    className="ml-0.5 inline-flex min-w-5 items-center justify-center rounded-full bg-red-bg px-1.5 text-chip font-semibold text-red-fg"
                    // Announced, because the whole point is that an error on a
                    // hidden pane must not be discoverable only by looking.
                    aria-label={`${String(errors)} ${errors === 1 ? 'problem' : 'problems'}`}
                  >
                    {errors}
                  </span>
                ) : tab.dirty === true ? (
                  <span
                    className="ml-0.5 size-1.5 rounded-full bg-gold-deep"
                    aria-label="unsaved changes"
                  />
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      {tabs.map((tab) => (
        <div
          key={tab.id}
          ref={(node) => {
            panelsRef.current[tab.id] = node;
          }}
          role="tabpanel"
          id={`${baseId}-panel-${tab.id}`}
          aria-labelledby={`${baseId}-tab-${tab.id}`}
          // Mounted always, hidden with CSS. Unmounting would discard input
          // state on every tab change and drop the validation this relies on.
          // `hidden` (not just a class) keeps hidden fields out of the tab order
          // and out of the accessibility tree.
          hidden={tab.id !== activeId}
          className="flex min-w-0 flex-col gap-4 pt-1"
        >
          {tab.content}
        </div>
      ))}
    </div>
  );
}
